// src/pages/mairie/GestionCollecteurs.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionCollecteurs() {
    const [collecteurs, setCollecteurs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        numero: '',
        nb_carnets: 0
    });
    const [deleting, setDeleting] = useState(null);

    const fetchCollecteurs = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/collecteurs.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setCollecteurs(data.collecteurs);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCollecteurs();
    }, []);

    const resetForm = () => {
        setFormData({ nom: '', prenom: '', numero: '', nb_carnets: 0 });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nom.trim() || !formData.prenom.trim() || !formData.numero.trim()) {
            toast.error('Tous les champs sont obligatoires');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const payload = { ...formData };
            if (editingId) payload.id_collecteur = editingId;

            const res = await fetch(`${config.apiBase}/mairie/collecteurs.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                toast.success(editingId ? 'Collecteur modifié' : 'Collecteur ajouté');
                resetForm();
                fetchCollecteurs();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (collecteur) => {
        setFormData({
            nom: collecteur.nom,
            prenom: collecteur.prenom,
            numero: collecteur.numero,
            nb_carnets: collecteur.nb_carnets || 0
        });
        setEditingId(collecteur.id_collecteur);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce collecteur ?')) return;
        setDeleting(id);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/collecteurs.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id_collecteur: id })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Collecteur supprimé');
                fetchCollecteurs();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setDeleting(null);
        }
    };

    const toggleForm = () => {
        if (showForm) {
            resetForm();
        } else {
            setShowForm(true);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold">👥 Gestion des collecteurs</h1>
                    <p className="text-sm text-gray-500">Gérez les collecteurs autorisés</p>
                </div>
                <button
                    onClick={toggleForm}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                >
                    {showForm ? '✕ Annuler' : '+ Nouveau collecteur'}
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-lg border mb-6">
                    <h2 className="text-lg font-semibold mb-4">
                        {editingId ? '✏️ Modifier le collecteur' : '📝 Nouveau collecteur'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                            <input
                                type="text"
                                value={formData.nom}
                                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                            <input
                                type="text"
                                value={formData.prenom}
                                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Numéro *</label>
                            <input
                                type="text"
                                value={formData.numero}
                                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de carnets</label>
                            <input
                                type="number"
                                value={formData.nb_carnets}
                                onChange={(e) => setFormData({ ...formData, nb_carnets: parseInt(e.target.value) || 0 })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 outline-none"
                                min="0"
                            />
                            <p className="text-xs text-gray-400 mt-1">Nombre de carnets de reçus associés</p>
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                            {loading ? 'Enregistrement...' : editingId ? '💾 Modifier' : '➕ Ajouter'}
                        </button>
                        <button 
                            type="button" 
                            onClick={resetForm} 
                            className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition"
                        >
                            Annuler
                        </button>
                    </div>
                </form>
            )}

            {loading && !showForm ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-green-600"></div>
                </div>
            ) : collecteurs.length === 0 ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                    <div className="text-6xl mb-4">👥</div>
                    <p className="text-gray-500 text-lg">Aucun collecteur enregistré</p>
                    <p className="text-sm text-gray-400">Cliquez sur "Nouveau collecteur" pour en ajouter</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {collecteurs.map(collecteur => (
                        <div key={collecteur.id_collecteur} className="bg-white rounded-xl shadow-lg border p-5 hover:shadow-xl transition">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="font-bold text-lg text-gray-800">{collecteur.prenom} {collecteur.nom}</h3>
                                    <p className="text-sm text-gray-500">📱 {collecteur.numero}</p>
                                    <div className="flex items-center gap-3 mt-1">
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                            📋 {collecteur.nb_carnets || 0} carnets
                                        </span>
                                        <span className="text-xs text-gray-400">ID: #{collecteur.id_collecteur}</span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 ml-2">
                                    <button 
                                        onClick={() => handleEdit(collecteur)} 
                                        className="bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 transition text-sm"
                                    >
                                        ✏️ Modifier
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(collecteur.id_collecteur)} 
                                        disabled={deleting === collecteur.id_collecteur}
                                        className="bg-red-50 text-red-600 px-3 py-1 rounded hover:bg-red-100 transition text-sm disabled:opacity-50"
                                    >
                                        {deleting === collecteur.id_collecteur ? '...' : '🗑️ Supprimer'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {collecteurs.length > 0 && (
                <div className="mt-6 bg-white rounded-xl shadow-lg p-4 border">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>📊 Total: <strong>{collecteurs.length}</strong> collecteurs</span>
                        <span>📋 Total carnets: <strong>{collecteurs.reduce((sum, c) => sum + (c.nb_carnets || 0), 0)}</strong></span>
                    </div>
                </div>
            )}
        </div>
    );
}